/** Locale/timezone aware formatting (ADR 0008-0010). Locale and timezone follow the signed-in user. */
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

let currentLocale = navigator.language || "en-US";
let currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function configureFormatting({ locale, timezone: tz }: { locale?: string; timezone?: string }) {
  if (locale) currentLocale = locale;
  if (tz) currentTimezone = tz;
}

export function getLocale() {
  return currentLocale;
}

export function formatMoney(amount: string | number | null | undefined, currency: string, minor?: number) {
  if (amount === null || amount === undefined || amount === "") return "—";
  const n = typeof amount === "string" ? Number(amount) : amount;
  try {
    return new Intl.NumberFormat(currentLocale, {
      style: "currency", currency, minimumFractionDigits: minor, maximumFractionDigits: minor,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
}

export function formatQuantity(value: string | number | null | undefined, uom?: string, maxDecimals = 4) {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  const s = new Intl.NumberFormat(currentLocale, { maximumFractionDigits: maxDecimals }).format(n);
  return uom ? `${s} ${uom}` : s;
}

export function formatNumber(value: number | string | null | undefined, maxDecimals = 2) {
  if (value === null || value === undefined || value === "") return "—";
  return new Intl.NumberFormat(currentLocale, { maximumFractionDigits: maxDecimals }).format(Number(value));
}

export function formatDate(value: string | Date | null | undefined) {
  if (!value) return "—";
  return dayjs(value).tz(currentTimezone).format("YYYY-MM-DD");
}

export function formatDateTime(value: string | Date | null | undefined) {
  if (!value) return "—";
  return dayjs(value).tz(currentTimezone).format("YYYY-MM-DD HH:mm");
}

export function formatRelative(value: string | Date | null | undefined) {
  if (!value) return "—";
  const diff = dayjs().diff(dayjs(value), "minute");
  if (Math.abs(diff) < 1) return "just now";
  if (Math.abs(diff) < 60) return `${diff} min ago`;
  const hours = Math.round(diff / 60);
  if (Math.abs(hours) < 24) return `${hours} h ago`;
  return formatDate(value);
}
